/**
 * Diagnostic de programme (migration 0318) — client des RPC + helpers PURS.
 *
 * Toujours un diagnostic actif par élève : créé à la 1re visite du tableau de
 * bord (`ensure_skill_diagnostic`) puis à chaque rentrée. ~15 questions des
 * SKILLCARDS déjà vues (début de l'année en cours + année précédente), à
 * arrêter / reprendre / recommencer. XP élève, aucune incidence sur l'HORA.
 * Le rapport IA est rédigé par l'edge fn `skill-diagnostic-report`.
 */

import { publishProgress } from '@/lib/progress-sync';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

const db = supabase as unknown as SupabaseClient;

export type DiagnosticStatus = 'pending' | 'in_progress' | 'completed' | 'abandoned';
export type DiagnosticKind = 'onboarding' | 'rentree' | 'manual';

export interface DiagnosticSummary {
  id: string;
  kind: DiagnosticKind;
  status: DiagnosticStatus;
  school_year: number;
  question_count: number;
  answered_count: number;
  total: number | null;
  correct: number | null;
  mastery_pct: number | null;
  xp_awarded: number | null;
  prompted_at: string | null;
  started_at: string | null;
  completed_at: string | null;
  has_report: boolean;
  /** Réponses couvertes par le rapport (partiel si < réponses données). */
  report_answered?: number | null;
  /** Date à partir de laquelle un nouveau diagnostic est possible (1 mois). */
  restart_available_at?: string | null;
}

export interface DiagnosticState {
  available: boolean;
  reason?: string;
  current: DiagnosticSummary | null;
  last_completed: DiagnosticSummary | null;
}

export interface DiagnosticChoice {
  id: string;
  display_order: number;
  label_md: string;
  is_correct: boolean;
  feedback_md: string | null;
}

export interface DiagnosticQuestion {
  step_id: string;
  card_id: string | null;
  card_title: string | null;
  prompt_md: string;
  /** Visuels de l'énoncé de contexte + de la question (migration 0342). */
  visuals?: unknown[];
  explanation_md: string | null;
  choices: DiagnosticChoice[];
}

export interface DiagnosticSession {
  diagnostic: DiagnosticSummary;
  questions: DiagnosticQuestion[];
  answered: { question_id: string; was_correct: boolean }[];
}

export interface DiagnosticReport {
  summary_md: string;
  strengths: string[];
  to_consolidate: string[];
  gaps: string[];
  advice: string[];
}

export interface DiagnosticCardResult {
  card_id: string;
  correct: number;
  total: number;
  needs_work: boolean;
  title: string | null;
  domain_label: string | null;
  levels: string[];
  color_theme: string | null;
  creature: unknown;
}

export interface DiagnosticResult {
  diagnostic: DiagnosticSummary;
  /** Diagnostic en pause : résultat et rapport sur les réponses déjà données. */
  partial?: boolean;
  report: DiagnosticReport | null;
  cards: DiagnosticCardResult[];
}

// ── Helpers purs ────────────────────────────────────────────────────────────

/** Réponses minimum pour un rapport (partiel) — miroir de l'edge fn. */
export const DIAGNOSTIC_REPORT_MIN_ANSWERS = 5;

/** Le rapport (partiel ou final) est disponible : au moins 5 réponses. */
export function canSeeReport(d: Pick<DiagnosticSummary, 'answered_count'> | null | undefined) {
  return !!d && d.answered_count >= DIAGNOSTIC_REPORT_MIN_ANSWERS;
}

/** Diagnostic dont le rapport est à montrer : le courant s'il en a un, sinon le dernier terminé. */
export function reportDiagnostic(state: DiagnosticState | null | undefined) {
  if (canSeeReport(state?.current)) return state!.current;
  if (canSeeReport(state?.last_completed)) return state!.last_completed;
  return null;
}

/** Un nouveau diagnostic est possible (1 mois après le début du précédent). */
export function canRestartDiagnostic(
  d: Pick<DiagnosticSummary, 'restart_available_at'> | null | undefined,
  now: Date = new Date(),
): boolean {
  if (!d?.restart_available_at) return true;
  return now.getTime() >= Date.parse(d.restart_available_at);
}

/** « 7 novembre » : date du prochain diagnostic possible. */
export function restartDateLabel(iso: string | null | undefined): string {
  if (!iso) return '';
  return new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long' }).format(new Date(iso));
}

/**
 * Proposer le diagnostic en fenêtre au moins UNE fois : diagnostic courant pas
 * commencé et jamais proposé.
 */
export function shouldPromptDiagnostic(state: DiagnosticState | null | undefined): boolean {
  const c = state?.current;
  return !!state?.available && !!c && c.status === 'pending' && !c.prompted_at;
}

/** Libellé du bouton principal du diagnostic courant. */
export function diagnosticCta(current: DiagnosticSummary | null | undefined): string {
  if (!current) return 'Faire le diagnostic';
  if (current.status === 'in_progress') {
    return `Reprendre (${current.answered_count}/${current.question_count})`;
  }
  if (current.status === 'completed') return 'Voir mon diagnostic';
  return 'Faire le diagnostic';
}

/** Index de la 1re question sans réponse (reprise), ou la longueur si tout est répondu. */
export function resumeIndex(session: Pick<DiagnosticSession, 'questions' | 'answered'>): number {
  const done = new Set(session.answered.map((a) => a.question_id));
  const i = session.questions.findIndex((q) => !done.has(q.step_id));
  return i < 0 ? session.questions.length : i;
}

export function diagnosticErrorMessage(code: string): string {
  switch (code) {
    case 'NO_QUESTIONS_AVAILABLE':
      return 'Pas encore assez de SKILLCARDS pour ton niveau : le diagnostic arrive bientôt.';
    case 'NO_LEVEL':
      return 'Indique ton niveau dans ton profil pour faire le diagnostic.';
    case 'DIAGNOSTIC_NOT_FOUND':
      return 'Diagnostic introuvable. Relance-le depuis ton tableau de bord.';
    case 'NO_ANSWERS':
      return 'Réponds au moins à une question avant de terminer.';
    case 'NOT_ALLOWED':
      return 'Ce bilan est réservé aux adultes : les élèves ont leur diagnostic de programme.';
    case 'RESTART_TOO_SOON':
      return 'Un nouveau diagnostic sera possible un mois après le précédent.';
    default:
      return 'Une erreur est survenue. Réessaie.';
  }
}

export class DiagnosticError extends Error {
  constructor(public readonly code: string) {
    super(code);
    this.name = 'DiagnosticError';
  }
}

function unwrap<T>(data: unknown, error: { message: string } | null): T {
  if (error) throw new DiagnosticError('NETWORK_ERROR');
  if (data && typeof data === 'object' && 'error' in data) {
    throw new DiagnosticError(String((data as { error: string }).error));
  }
  return data as T;
}

// ── Hooks ───────────────────────────────────────────────────────────────────

export const diagnosticStateKey = (userId: string) => ['skill-diagnostic', userId] as const;

/** État du diagnostic (crée le diagnostic du moment s'il manque). */
export function useDiagnosticState(userId: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: diagnosticStateKey(userId ?? ''),
    enabled: !!userId && enabled,
    staleTime: 30_000,
    queryFn: async (): Promise<DiagnosticState> => {
      const { data, error } = await db.rpc('ensure_skill_diagnostic');
      return unwrap<DiagnosticState>(data, error);
    },
  });
}

export async function startDiagnostic(restart = false): Promise<DiagnosticSession> {
  const { data, error } = await db.rpc('start_skill_diagnostic', { p_restart: restart });
  return unwrap<DiagnosticSession>(data, error);
}

export async function submitDiagnosticAnswer(input: {
  diagnosticId: string;
  questionId: string;
  choiceId: string;
  timeMs: number;
}): Promise<{ was_correct: boolean; explanation_md: string | null; feedback_md: string | null }> {
  const { data, error } = await db.rpc('submit_skill_diagnostic_answer', {
    p_diagnostic_id: input.diagnosticId,
    p_question_id: input.questionId,
    p_choice_id: input.choiceId,
    p_time_ms: input.timeMs,
  });
  return unwrap(data, error);
}

export async function finishDiagnostic(diagnosticId: string): Promise<DiagnosticResult> {
  const { data, error } = await db.rpc('finish_skill_diagnostic', {
    p_diagnostic_id: diagnosticId,
  });
  const result = unwrap<DiagnosticResult>(data, error);
  publishProgress('session-finished');
  return result;
}

export function useDiagnosticResult(diagnosticId: string | null | undefined) {
  return useQuery({
    queryKey: ['skill-diagnostic-result', diagnosticId],
    enabled: !!diagnosticId,
    queryFn: async (): Promise<DiagnosticResult> => {
      const { data, error } = await db.rpc('skill_diagnostic_result', {
        p_diagnostic_id: diagnosticId,
      });
      return unwrap<DiagnosticResult>(data, error);
    },
  });
}

/**
 * Rapport IA (partiel dès 5 réponses). Idempotent côté serveur : renvoie celui
 * qui existe s'il couvre déjà toutes les réponses, sinon le réécrit.
 */
export function useGenerateDiagnosticReport(diagnosticId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<DiagnosticReport> => {
      const { data, error } = await supabase.functions.invoke('skill-diagnostic-report', {
        body: { diagnostic_id: diagnosticId },
      });
      if (error) throw new DiagnosticError('REPORT_FAILED');
      return (data as { report: DiagnosticReport }).report;
    },
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: ['skill-diagnostic-result', diagnosticId] }),
  });
}

export async function markDiagnosticPrompted(diagnosticId: string): Promise<void> {
  await db.rpc('mark_skill_diagnostic_prompted', { p_diagnostic_id: diagnosticId });
}

// ── Bilan d'un adulte : « Veux-tu t'évaluer ? » (migration 0328) ────────────

/** Niveaux proposés à un adulte (« Quel niveau penses-tu avoir ? »). */
export const ADULT_LEVEL_CHOICES = [
  { value: '6e', label: '6ème' },
  { value: '5e', label: '5ème' },
  { value: '4e', label: '4ème' },
  { value: '3e', label: '3ème' },
  { value: '2nde', label: 'Seconde' },
  { value: '1re', label: 'Première' },
  { value: 'tle', label: 'Terminale' },
] as const;
export type AdultLevel = (typeof ADULT_LEVEL_CHOICES)[number]['value'];

export function adultLevelLabel(level: string | null | undefined): string {
  return ADULT_LEVEL_CHOICES.find((l) => l.value === level)?.label ?? level ?? '';
}

export interface AdultDiagnosticSummary extends DiagnosticSummary {
  level: string | null;
}

export interface AdultDiagnosticState {
  available: boolean;
  role?: string;
  in_progress?: AdultDiagnosticSummary | null;
  last_completed?: AdultDiagnosticSummary | null;
}

/** Textes de l'écran d'accueil selon le public (parent / curieux). */
export function adultIntroCopy(role: string | null | undefined): {
  title: string;
  lead: string;
  levelQuestion: string;
} {
  if (role === 'parent') {
    return {
      title: 'Voulez-vous vous évaluer ?',
      lead: 'Connaître votre niveau en maths aujourd’hui, pour savoir sur quoi vous pouvez aider votre enfant — et ce qu’il vaut mieux revoir avant. 15 questions, vous arrêtez quand vous voulez.',
      levelQuestion: 'Quel niveau pensez-vous avoir ?',
    };
  }
  return {
    title: 'Veux-tu t’évaluer ?',
    lead: 'Connaître ton niveau en maths aujourd’hui : 15 questions sur le programme du niveau que tu choisis et des années d’avant. Tu arrêtes quand tu veux, un bilan t’attend à la fin.',
    levelQuestion: 'Quel niveau penses-tu avoir ?',
  };
}

export const adultDiagnosticStateKey = (userId: string) => ['adult-diagnostic', userId] as const;

export function useAdultDiagnosticState(userId: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: adultDiagnosticStateKey(userId ?? ''),
    enabled: !!userId && enabled,
    staleTime: 30_000,
    queryFn: async (): Promise<AdultDiagnosticState> => {
      const { data, error } = await db.rpc('get_adult_diagnostic_state');
      return unwrap<AdultDiagnosticState>(data, error);
    },
  });
}

export async function startAdultDiagnostic(
  level: AdultLevel,
  restart = false,
): Promise<DiagnosticSession> {
  const { data, error } = await db.rpc('start_adult_diagnostic', {
    p_level: level,
    p_restart: restart,
  });
  return unwrap<DiagnosticSession>(data, error);
}
